import React from 'react'
import styled, { css } from 'styled-components'
import { Link as RouterLink } from 'react-router-dom'
import { Property } from 'csstype'

import { ThemeColor } from 'theme/colors'
import { BoxProps, customBoxProps, BoxStyles } from 'components/styled/containers/box'
import {
  StyledH1,
  StyledH2,
  StyledH3,
  StyledLabel,
  StyledBody,
  StyledInfo,
  StyledSmall,
  StyledSubtitle,
  getSize,
} from './styles'
import { ThemeSpace } from 'theme/spaces'

export const Ellipsis = css`
  overflow: hidden;
  text-overflow: ellipsis;
`

export const LineClamp = css<{ numberOfLines?: number }>`
  display: -webkit-box;
  word-break: break-word;
  -webkit-line-clamp: ${({ numberOfLines }) => numberOfLines};
  -webkit-box-orient: vertical;
`

const BottomMarginStyle = css<{ mb?: ThemeSpace }>`
  &:not(:last-of-type) {
    margin-bottom: ${({ theme, mb }) =>
      mb ? theme.space[mb] : theme.space.s16};
  }
`

interface TextProps extends BoxProps {
  color?: ThemeColor
  name?: string
  fontWeight?: 300 | 400 | 600 | 700
  ellipsis?: boolean
  whiteSpace?: Property.WhiteSpace
  textAlign?: Property.TextAlign
  fontStyle?: Property.FontStyle
  numberOfLines?: number
  textTransform?: 'uppercase' | 'lowercase' | 'capitalize' | 'none'
}

const customProps = [
  'color',
  'name',
  'fontWeight',
  'ellipsis',
  'whiteSpace',
  'textAlign',
  'fontStyle',
  'numberOfLines',
  'textTransform',
  ...customBoxProps,
]

export const StyledText = styled.div.withConfig({
  shouldForwardProp: prop => !customProps.includes(prop),
})<TextProps>`
  ${BoxStyles};

  color: ${({ theme, color }) => color && theme.color[color]};
  ${({ ellipsis }) => ellipsis && Ellipsis};
  white-space: ${({ whiteSpace }) => whiteSpace};
  font-weight: ${({ fontWeight }) => fontWeight};
  text-align: ${({ textAlign }) => textAlign};
  font-style: ${({ fontStyle }) => fontStyle};
  ${({ numberOfLines }) => numberOfLines && LineClamp};
  word-break: break-word;
  text-transform: ${({ textTransform }) => textTransform};

  ${({ name }) => (name === 'body' || name === 'small') && BottomMarginStyle}
`

const TextComponent: React.FC<
  TextProps & {
    // eslint-disable-next-line
    as: any
  }
> = ({
  as,
  name = '',
  color,
  fontWeight,
  ellipsis,
  whiteSpace,
  textAlign,
  fontStyle,
  numberOfLines,
  children,
  ...props
}) => (
  <StyledText
    as={as}
    name={name}
    color={color}
    fontWeight={fontWeight}
    ellipsis={ellipsis}
    whiteSpace={whiteSpace}
    textAlign={textAlign}
    fontStyle={fontStyle}
    numberOfLines={numberOfLines}
    {...props}
  >
    {children}
  </StyledText>
)

export const H1: React.FC<TextProps> = props =>
  TextComponent({
    as: StyledH1,
    ...props,
  })
export const H2: React.FC<TextProps> = props =>
  TextComponent({
    as: StyledH2,
    ...props,
  })
export const H3: React.FC<TextProps> = props =>
  TextComponent({
    as: StyledH3,
    ...props,
  })
export const Info: React.FC<TextProps> = props =>
  TextComponent({
    as: StyledInfo,
    ...props,
  })
export const Subtitle: React.FC<TextProps> = props =>
  TextComponent({
    as: StyledSubtitle,
    ...props,
  })
export const Body: React.FC<TextProps> = props =>
  TextComponent({
    as: StyledBody,
    name: 'body',
    ...props,
  })
export const Small: React.FC<TextProps> = props =>
  TextComponent({
    as: StyledSmall,
    name: 'small',
    ...props,
  })
export const Label: React.FC<TextProps> = props =>
  TextComponent({
    as: StyledLabel,
    ...props,
  })

export const Hr = styled.hr`
  margin: ${({ theme }) => theme.space.s20} 0;
  border-top: 1px solid ${({ theme }) => theme.color.grey20};
`

export const LinkStyle = css`
  font-size: ${({ theme }) => `${getSize(theme.font.primary, 1.4)}rem`};
  text-decoration: underline;
  cursor: pointer;
  font-weight: 400;
  font-family: ${({ theme }) => theme.font.primary};

  && {
    color: ${({ theme }) => theme.color.bodyText};
  }

  &:hover {
    color: ${({ theme }) => theme.color.primary110};
  }

  &.focus-visible {
    color: ${({ theme }) => theme.color.primary110};
  }

  &:disabled {
    color: ${({ theme }) => theme.color.grey30};
    text-decoration: none;
  }
`
export const PrimaryLinkStyle = css`
  cursor: pointer;
  font-size: ${({ theme }) => `${getSize(theme.font.primary, 1.4)}rem`};
  font-weight: 400;
  font-family: ${({ theme }) => theme.font.primary};
  text-decoration: none;
  line-height: 1;
  width: fit-content;

  && {
    color: ${({ theme }) => theme.color.primary110};
  }

  &:hover {
    color: ${({ theme }) => theme.color.primary110};
    text-decoration: underline;
  }

  &.focus-visible {
    color: ${({ theme }) => theme.color.primary110};
    text-decoration: underline;
  }
`

export const SecondaryLinkStyle = css`
  cursor: pointer;
  font-weight: 400;
  font-size: ${({ theme }) => `${getSize(theme.font.primary, 1.4)}rem`};
  font-family: ${({ theme }) => theme.font.primary};
  text-decoration: none;

  && {
    color: ${({ theme }) => theme.color.bodyText};
  }

  &:hover {
    color: ${({ theme }) => theme.color.bodyText};
    text-decoration: underline;
  }

  &.focus-visible {
    color: ${({ theme }) => theme.color.bodyText};
    text-decoration: underline;
  }
`

export const Link = styled(RouterLink)`
  ${LinkStyle};
`

export const PrimaryLink = styled(RouterLink)`
  ${PrimaryLinkStyle};
`

export const StyledList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.space.s12};
`

export const StyledListItem = styled.li`
  width: 100%;
  margin: ${({ theme }) => theme.space.s4} 0;
  padding: 0;
`

export const ExternalLink = styled.a`
  ${LinkStyle};
`

export const ExternalPrimaryLink = styled.a`
  ${PrimaryLinkStyle};
`

export const LinkStyleButton = styled.button`
  padding: 0;
  ${LinkStyle};
`

export const PrimaryLinkStyleButton = styled.button`
  padding: 0;
  text-align: left;
  ${PrimaryLinkStyle};

  &:disabled {
    color: ${({ theme }) => theme.color.grey30};
    text-decoration: none;
  }
`

export const SecondaryLinkStyleButton = styled.button`
  padding: 0;
  ${SecondaryLinkStyle};

  &:disabled {
    color: ${({ theme }) => theme.color.grey30};
    text-decoration: none;
  }
`